import { AlertController, ToastController } from '@ionic/angular';
import { Injectable } from '@angular/core';
import { LabelsService } from '@services/labels/labels.service';
import { AppService } from '@services/app/app.service';
import { Network } from '@capacitor/network';
import { TimeInvokingService } from '../time-invoking/time-invoking.service';

@Injectable({
  providedIn: 'root'
})
export class NetworkStateService {
  /**
   * stato di connessione
   */
  private connected = true;

  /**
   * elementi UI per gestire gli stati della connessione
   */
  private connectionElements: {
    alert?: HTMLIonAlertElement;
    toast?: HTMLIonToastElement;
  } = {};

  constructor(
    private labelsService: LabelsService,
    private alertController: AlertController,
    private toastController: ToastController,
    private appService: AppService,
    private timeInvokingService: TimeInvokingService
  ) {}

  /**
   * avvia ascolto dello stato della connessione e mostra avvisi
   */
  async listenConnectionLoss(): Promise<void> {
    if (!this.appService.getConfig().generic.notifyConnectionLoss) {
      return;
    }

    this.timeInvokingService.invokeOnce(
      'NetworkStateServiceListenConnectionLoss',
      async () => {
        // creo subito gli elementi in modo che i moduli ionic
        // necessari vengano caricati subito e siano disponibile quando si perde la connessione
        await this.createConnectionElements();

        Network.addListener('networkStatusChange', status => {
          this.updateConnected(status.connected);
        });
      }
    );
  }

  /**
   * crea gli elementi da utilizzare per mostrare i cambi di stato della connessione
   */
  private async createConnectionElements(): Promise<void> {
    this.connectionElements.alert = await this.alertController.create({
      backdropDismiss: false,
      cssClass: 'connection-alert',
      header: this.labelsService.getLabel('msg.connection_off_title'),
      message: this.labelsService.getLabel('msg.connection_off_message')
    });

    this.connectionElements.toast = await this.toastController.create({
      message: this.labelsService.getLabel('msg.connection_on_title'),
      duration: 2000,
      position: 'top',
      color: 'success'
    });
  }

  /**
   * aggiorna lo stato di connessione e mostra i relativi warning
   */
  private async updateConnected(value: boolean): Promise<void> {
    const prevConnected = this.connected;

    this.connected = value;

    if (prevConnected && !this.connected) {
      await this.notifyOffline();
    } else if (!prevConnected && this.connected) {
      await this.notifyOnline();
    }
  }

  /**
   * notifica lo stato di offline
   */
  private async notifyOffline(): Promise<void> {
    this.updateConnectionLabels();
    await this.connectionElements.alert.present();
  }

  /**
   * notifica lo stato di online
   */
  private async notifyOnline(): Promise<void> {
    this.updateConnectionLabels();
    await this.connectionElements.alert.dismiss();
    await this.connectionElements.toast.present();
  }

  /**
   * aggiorna le label degli elementi di avvisso disconnessione
   * (quando vengono creati le label potrebbero non essere presenti)
   */
  private updateConnectionLabels(): void {
    this.connectionElements.alert.header = this.labelsService.getLabel(
      'msg.connection_off_title'
    );
    this.connectionElements.alert.message = this.labelsService.getLabel(
      'msg.connection_off_message'
    );
    this.connectionElements.toast.message = this.labelsService.getLabel(
      'msg.connection_on_title'
    );
  }
}
